import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Shield } from 'lucide-react';
import { getMCPServerRisk } from '@kirin/api-client/api/mcp';
import {
  mcpRiskLevelColors,
  mcpRiskLevelLabels,
  type MCPServerRisk,
} from '@kirin/api-client/contracts/mcp';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { apiClient } from '@/lib/api-client';
import { formatRelativeTime } from '@/lib/date-utils';
import { queryKeys } from '@/hooks/api/queryKeys';
import { McpRiskBadge } from './McpRiskBadge';
import { mcpRiskSubjectCopy } from './mcpRiskCopy';

/** Delay before a hover-opened card closes, so the pointer can reach it. */
const HOVER_CLOSE_DELAY_MS = 150;

interface McpRiskDetailsCardProps {
  organizationId: string;
  /** Row identity: the `.mcp.json` `config_name`. */
  name: string;
  /** The row's value from the list, shown on the trigger. */
  risk: MCPServerRisk;
}

/**
 * The Risk column's value with its "Risk assessment" card (KIR-5108). Opens
 * on hover or click; the detail (risk factors, report link, per-version
 * values) is fetched only once the card opens. AgentMesh owns the value —
 * there is no numeric score.
 */
export function McpRiskDetailsCard({
  organizationId,
  name,
  risk,
}: McpRiskDetailsCardProps): ReactElement {
  const [open, setOpen] = useState(false);
  // A click pins the card open until the next click or an outside dismiss.
  const [pinned, setPinned] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A hover-opened card must not move focus (Radix focuses the content on
  // open and the trigger on close), or skimming the column would pull focus
  // out of e.g. the search box. Click/keyboard opens keep Radix's focus
  // handling so the card is reachable and Escape returns to the trigger.
  const openedByHover = useRef(false);

  const cancelClose = (): void => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = (): void => {
    if (pinned) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };
  useEffect(() => cancelClose, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.mcp.risk(organizationId, name),
    queryFn: ({ signal }) =>
      getMCPServerRisk(apiClient, organizationId, name, { signal }),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const shown = data?.risk ?? risk;
  const factors = (data?.riskFactors ?? []).slice(0, 5);
  const versions = data?.versions ?? [];

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) setPinned(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Risk: ${mcpRiskLevelLabels[risk.level]}. Show risk assessment`}
          className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onMouseEnter={() => {
            cancelClose();
            if (!open) openedByHover.current = true;
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          onClick={event => {
            // Gateway rows are click-to-expand (KIR-3940); don't toggle them.
            event.stopPropagation();
            event.preventDefault();
            cancelClose();
            if (!open) openedByHover.current = false;
            const nextPinned = !pinned;
            setPinned(nextPinned);
            setOpen(nextPinned || !open);
          }}
        >
          <McpRiskBadge level={risk.level} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={event => {
          if (openedByHover.current) event.preventDefault();
        }}
        onCloseAutoFocus={event => {
          if (openedByHover.current) event.preventDefault();
        }}
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Risk assessment</h3>
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
            <Shield className="size-3" aria-hidden="true" />
            From AgentMesh
          </span>
        </div>

        <div className="space-y-1">
          <McpRiskBadge level={shown.level} size="lg" />
          <p className="text-xs text-muted-foreground">
            Last synced {formatRelativeTime(shown.lastSyncedAt)}
          </p>
          {shown.subject && (
            <p className="text-xs text-muted-foreground">
              {mcpRiskSubjectCopy(shown.subject)}
            </p>
          )}
        </div>

        {isLoading && (
          <p className="text-xs text-muted-foreground">Loading details…</p>
        )}
        {isError && (
          <p className="text-xs text-muted-foreground">
            Could not load the details from AgentMesh.
          </p>
        )}

        {factors.length > 0 && (
          <div className="space-y-1.5">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Top risk factors
            </h4>
            <ul className="space-y-1">
              {factors.map((factor, index) => (
                // Index in the key: AgentMesh may repeat a factor string.
                <li
                  key={`${index}:${factor}`}
                  className="flex items-start gap-2 text-xs"
                >
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-1.5 shrink-0 rounded-full"
                    style={{
                      backgroundColor: mcpRiskLevelColors[shown.level],
                    }}
                  />
                  <span className="break-words">{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {versions.length > 1 && (
          <div className="space-y-1.5">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Installed versions
            </h4>
            <ul className="space-y-1">
              {versions.map(v => (
                <li
                  key={`${v.ecosystem}:${v.packageName}@${v.version}`}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="min-w-0 truncate">
                    {v.packageName}@{v.version}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {v.risk ? (
                      <McpRiskBadge level={v.risk.level} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    <span className="text-muted-foreground">
                      {v.userCount} {v.userCount === 1 ? 'user' : 'users'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data?.agentmeshUrl && (
          <div className="border-t pt-2">
            <a
              href={data.agentmeshUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              <ExternalLink className="size-3.5" aria-hidden="true" />
              View full report in AgentMesh
            </a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
